import { yupResolver } from '@hookform/resolvers/yup';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import {
  AppRoutes,
  ERROR_PASSWORD_RESET_LIMIT,
  LOGIN_INPUT_CLASSES,
  LOGIN_SUBMIT_BUTTON_CLASSES,
} from '@/constants';
import { useForgotPassword } from '@/hooks';
import { IForgotPasswordPayload } from '@/interfaces';
import { navigate } from '@/providers';

import { AuthGlassContainer, Button, Input } from '@/components/common';
import { Loading02Icon } from '@/assets/icons';

import { FORGOT_PASSWORD_FORM_FIELDS, FORGOT_PASSWORD_SCHEMA } from '@/features/auth/constants';
import { isValidEmail } from '@/features/auth/utils';

import { ResetLinkStatus } from './ResetLinkStatus';

type ResetStatus = 'idle' | 'success' | 'error';

export const ForgotPassword = () => {
  const { mutateAsync: forgotPasswordMutation, isPending } = useForgotPassword();
  const [status, setStatus] = useState<ResetStatus>('idle');

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors },
  } = useForm<IForgotPasswordPayload>({
    resolver: yupResolver(FORGOT_PASSWORD_SCHEMA),
    mode: 'onTouched',
  });

  const onSubmit = async (data: IForgotPasswordPayload) => {
    await forgotPasswordMutation(data, {
      onSuccess: () => setStatus('success'),
      onError: (error: any) => {
        if (error.response?.data?.errorMessage === ERROR_PASSWORD_RESET_LIMIT) {
          setError(FORGOT_PASSWORD_FORM_FIELDS.email, {
            type: 'server',
            message: ERROR_PASSWORD_RESET_LIMIT,
          });
        } else {
          setStatus('error');
        }
      },
    });
  };

  if (status !== 'idle') {
    return <ResetLinkStatus isSuccess={status === 'success'} />;
  }

  return (
    <AuthGlassContainer className="w-[400px] flex flex-col items-center justify-center gap-8">
      <div className="flex flex-col gap-6 w-full">
        <span className="text-h6 text-neutral-0 font-bold uppercase text-center">
          SET UP PASSWORD
        </span>
        <span className="text-body-sm text-neutral-0 font-semibold text-center">
          Please enter your ST Engineering email to proceed
        </span>
        <Input
          className={LOGIN_INPUT_CLASSES}
          label="Email Address"
          required
          placeholder="Enter Email Address"
          hasSuccessIcon={isValidEmail(watch(FORGOT_PASSWORD_FORM_FIELDS.email))}
          error={errors[FORGOT_PASSWORD_FORM_FIELDS.email]?.message}
          {...register(FORGOT_PASSWORD_FORM_FIELDS.email)}
        />
      </div>
      <div className="flex flex-col gap-6 w-full">
        <Button
          buttonColor="primary"
          buttonStyle="filled"
          size="lg"
          className={LOGIN_SUBMIT_BUTTON_CLASSES}
          onClick={handleSubmit(onSubmit)}
          disabled={
            isPending ||
            !watch(FORGOT_PASSWORD_FORM_FIELDS.email) ||
            !isValidEmail(watch(FORGOT_PASSWORD_FORM_FIELDS.email))
          }
        >
          {isPending ? (
            <Loading02Icon className="size-6 text-opacity-light-30 animate-[spin_2s_linear_infinite]" />
          ) : (
            'Send Reset Link'
          )}
        </Button>
        <Button
          size="lg"
          buttonColor="neutral"
          buttonStyle="textlink"
          className="!text-neutral-5 disabled:!text-neutral-8"
          onClick={() => navigate(AppRoutes.LOGIN)}
          disabled={isPending}
        >
          Back to Login
        </Button>
      </div>
    </AuthGlassContainer>
  );
};
